import React, { useState } from "react";
import {
  BrowserRouter as Router,
  useNavigate,
  useLocation,
} from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./components/Toast";
import MyVideos from "./pages/MyVideos";
import Video from "./pages/Video";
import Image from "./pages/Image";
import Templates from "./pages/Templates";
import PromptExtractor from "./pages/PromptExtractor";
import Support from "./pages/Support";
import NoFilter from "./pages/NoFilter";
import NoFilterActors from "./pages/NoFilterActors";
import NoFilterExamples from "./pages/NoFilterExamples";
import NoFilter1 from "./pages/NoFilter1";
import NoFilter2 from "./pages/NoFilter2";
import NoFilter3 from "./pages/NoFilter3";
import NoFilter4 from "./pages/NoFilter4";
import HNoFilter from "./pages/HNoFilter";
import TopBar from "./components/navigation/TopBar";
import VideoGalleryModal from "./components/VideoGalleryModal";
import galleryVideosData from "./data/galleryVideos.json";

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenuItem, setActiveMenuItem] = useState<string>("dashboard");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedGalleryVideo, setSelectedGalleryVideo] = useState<any>(null);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);

  // Force dark mode to always be enabled
  React.useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  // Check if we're on a standalone page (no layout)
  const isStandalonePage = 
    location.pathname === "/no-filter-1" || 
    location.pathname === "/no-filter-2" || 
    location.pathname === "/no-filter-3" ||
    location.pathname === "/no-filter-4" ||
    location.pathname === "/h-no-filter";

  // Auto-redirect from dashboard to root and sync URL with state
  React.useEffect(() => {
    const path = location.pathname;
    if (path === "/dashboard") {
      const searchParams = location.search;
      const hash = location.hash;
      const fullPath = `/${searchParams}${hash}`;
      navigate(fullPath, { replace: true });
      return;
    }

    if (path === "/") {
      setActiveMenuItem("dashboard");
    } else if (path === "/assets") {
      setActiveMenuItem("assets");
    } else if (path === "/image" || path.startsWith("/image/")) {
      setActiveMenuItem("create-image");
    } else if (path === "/video" || path.startsWith("/video/")) {
      setActiveMenuItem("create-video");
    } else if (path === "/templates") {
      setActiveMenuItem("templates");
    } else if (path === "/billing") {
      setActiveMenuItem("billing");
    } else if (path === "/prompt-extractor") {
      setActiveMenuItem("prompt-extractor");
    } else if (path === "/support") {
      setActiveMenuItem("support");
    } else if (path === "/no-filter" || path.startsWith("/no-filter/")) {
      setActiveMenuItem("no-filter");
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  const handleMenuItemClick = (itemId: string) => {
    const menuActions = {
      dashboard: () => {
        setActiveMenuItem("dashboard");
        setIsSidebarOpen(false);
        navigate("/");
      },
      "create-video": () => {
        setActiveMenuItem("create-video");
        setIsSidebarOpen(false);
        navigate("/video/t2v");
      },
      "create-image": () => {
        setActiveMenuItem("create-image");
        setIsSidebarOpen(false);
        navigate("/image/t2i");
      },
      billing: () => {
        // Redirect to pixelmaker.ai billing
        window.location.href = "https://pixelmaker.ai/billing?utm_source=pixelmaker_io";
      },
      assets: () => {
        // Navigate to assets page
        setActiveMenuItem("assets");
        setIsSidebarOpen(false);
        navigate("/assets");
      },
      "prompt-extractor": () => {
        setActiveMenuItem("prompt-extractor");
        setIsSidebarOpen(false);
        navigate("/prompt-extractor");
      },
      support: () => {
        setActiveMenuItem("support");
        setIsSidebarOpen(false);
        navigate("/support");
      },
      "no-filter": () => {
        setActiveMenuItem("no-filter");
        setIsSidebarOpen(false);
        navigate("/no-filter");
      },
      "templates": () => {
        setActiveMenuItem("templates");
        setIsSidebarOpen(false);
        navigate("/templates");
      },
    };

    if (itemId === "dashboard") {
      menuActions.dashboard();
    } else if (itemId === "create-video") {
      menuActions["create-video"]();
    } else if (itemId === "create-image") {
      menuActions["create-image"]();
    } else if (itemId === "billing") {
      menuActions.billing();
    } else if (itemId === "assets") {
      menuActions.assets();
    } else if (itemId === "prompt-extractor") {
      menuActions["prompt-extractor"]();
    } else if (itemId === "support") {
      menuActions.support();
    } else if (itemId === "no-filter") {
      menuActions["no-filter"]();
    } else if (itemId === "templates") {
      menuActions["templates"]();
    }
  };

  const handleGalleryVideoClick = (video: any) => {
    setSelectedGalleryVideo(video);
    setIsGalleryModalOpen(true);
  };

  const handleGalleryModalClose = () => {
    setIsGalleryModalOpen(false);
    setSelectedGalleryVideo(null);
  };

  // Handle Make Video button click - navigate to video page
  const handleMakeVideoClick = () => {
    setActiveMenuItem("create-video");
    navigate("/video/t2v");
  };

  // Render standalone page without layout
  if (isStandalonePage) {
    const renderSqueezePageComponent = () => {
      if (location.pathname === "/no-filter-1") return <NoFilter1 />;
      if (location.pathname === "/no-filter-2") return <NoFilter2 />;
      if (location.pathname === "/no-filter-3") return <NoFilter3 />;
      if (location.pathname === "/no-filter-4") return <NoFilter4 />;
      if (location.pathname === "/h-no-filter") return <HNoFilter />;
      return null;
    };

    return <>{renderSqueezePageComponent()}</>;
  }

  return (
    <>
      <div className="h-screen bg-black dark:bg-black flex flex-col overscroll-none transition-colors duration-300">
        {/* Top Bar - Natural flow */}
        <div className="flex-shrink-0 z-50">
          <TopBar onMakeVideoClick={handleMakeVideoClick} />
        </div>

        {/* Mobile Header - improved styling - Natural flow below TopBar */}
        <div className="md:hidden flex-shrink-0 z-40 bg-black dark:bg-black border-b border-white/10 dark:border-white/10 transition-all duration-300 shadow-sm rounded-t-xl">
          <div className="flex items-center justify-between px-4 py-2">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-3 rounded-lg hover:bg-white/10 dark:hover:bg-white/10 active:bg-white/20 dark:active:bg-white/20 transition-colors"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 6h18M3 12h18M3 18h18"
                />
              </svg>
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  window.location.href = "https://pixelmaker.ai?utm_source=pixelmaker_io";
                }}
                className="bg-white hover:bg-gray-200 text-black rounded-full text-xs font-medium transition-colors flex items-center justify-center"
                style={{
                  height: "32px",
                  maxHeight: "32px",
                  minHeight: "32px",
                  lineHeight: "1",
                  padding: "0 20px",
                }}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area - Flex-1 to fill remaining space */}
        <div className="flex-1 flex min-h-0 bg-black dark:bg-black rounded-none md:rounded-tl-xl md:rounded-tr-xl shadow-none md:shadow-lg overflow-hidden transition-colors duration-300">
          {/* Desktop Sidebar */}
          <div className="hidden md:flex md:w-80 bg-black dark:bg-black border-r border-white/30 dark:border-white/30 flex-col transition-colors duration-300">
            {/* Navigation */}
            <div className="flex-1 px-4 pt-6 pb-4 overflow-y-auto scrollbar-hide">
              {/* Menu Section */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 px-3">
                  MENU
                </h3>
                <div className="space-y-2">
                  {/* Dashboard */}
                  <button
                    onClick={() => handleMenuItemClick("dashboard")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "dashboard"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    <span className="text-sm">Home</span>
                  </button>

                  {/* Assets */}
                  <button
                    onClick={() => handleMenuItemClick("assets")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "assets"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <span className="text-sm">Assets</span>
                  </button>

                  {/* Billing */}
                  <button
                    onClick={() => handleMenuItemClick("billing")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "billing"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <span className="text-sm">Billing</span>
                  </button>
                </div>
              </div>

              {/* Create Section */}
              <div className="mb-8">
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 px-3">
                  CREATE
                </h3>
                <div className="space-y-2">
                  {/* Video */}
                  <button
                    onClick={() => handleMenuItemClick("create-video")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "create-video"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm">Video</span>
                  </button>

                  {/* Image */}
                  <button
                    onClick={() => handleMenuItemClick("create-image")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "create-image"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span className="text-sm">Image</span>
                  </button>

                  {/* No Filter */}
                  <button
                    onClick={() => handleMenuItemClick("no-filter")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "no-filter"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                      />
                    </svg>
                    <span className="text-sm">No Filter</span>
                  </button>

                  {/* Templates */}
                  <button
                    onClick={() => handleMenuItemClick("templates")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "templates"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
                      />
                    </svg>
                    <span className="text-sm">Templates</span>
                  </button>
                </div>
              </div>

              {/* Explore Section */}
              <div>
                <h3 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5 px-3">
                  EXPLORE
                </h3>
                <div className="space-y-2">
                  {/* Prompt Extractor */}
                  <button
                    onClick={() => handleMenuItemClick("prompt-extractor")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "prompt-extractor"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="text-sm">Prompt Extractor</span>
                  </button>

                  {/* Support */}
                  <button
                    onClick={() => handleMenuItemClick("support")}
                    className={`w-full text-left px-3 py-2.5 rounded-full transition-all duration-200 flex items-center space-x-3 ${
                      activeMenuItem === "support"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-sm">Support</span>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Desktop Bottom User Section */}
            <div className="flex-shrink-0 p-4 border-t border-white/20 dark:border-white/20 bg-black dark:bg-black transition-colors duration-300">
              <div className="w-full flex items-center space-x-3 px-3 py-3 rounded-lg bg-white/5 dark:bg-white/5 border border-white/10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    Not Signed In
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Sign in to create videos
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Modal */}
          <div
            className={`
        md:hidden fixed inset-0 z-50 transition-all duration-300 ease-in-out
        ${isSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}
      `}
          >
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsSidebarOpen(false)}
            />

            {/* Modal Card */}
            <div
              className={`
              absolute bottom-0 left-0 right-0 bg-black dark:bg-black rounded-t-2xl transition-all duration-300 ease-in-out max-h-[85vh] border-t border-white/10
              ${isSidebarOpen ? "translate-y-0" : "translate-y-full"}
            `}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 pb-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Navigation
                </h2>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-1.5 rounded-full hover:bg-white/10 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 text-gray-500 dark:text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="px-4 pb-48 overflow-y-auto scrollbar-hide max-h-[70vh]">
                {/* Main Navigation */}
                <div className="space-y-2 mb-8">
                  <button
                    onClick={() => handleMenuItemClick("dashboard")}
                    className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                      activeMenuItem === "dashboard"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                    <span className="text-sm">Home</span>
                  </button>

                  <button
                    onClick={() => handleMenuItemClick("assets")}
                    className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                      activeMenuItem === "assets"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                    <span className="text-sm">Assets</span>
                  </button>

                  <button
                    onClick={() => handleMenuItemClick("billing")}
                    className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                      activeMenuItem === "billing"
                        ? "bg-white/10 dark:bg-white/10 text-white dark:text-white font-medium shadow-[0_0_15px_rgba(255,255,255,0.3)]"
                        : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <span className="text-sm">Billing</span>
                  </button>
                </div>

                {/* Create Section */}
                <div className="mb-8">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
                    Create
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleMenuItemClick("create-video")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "create-video"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Video</span>
                    </button>

                    <button
                      onClick={() => handleMenuItemClick("create-image")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "create-image"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-sm">Image</span>
                    </button>

                    <button
                      onClick={() => handleMenuItemClick("no-filter")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "no-filter"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      </svg>
                      <span className="text-sm">No Filter</span>
                    </button>

                    {/* Templates */}
                    <button
                      onClick={() => handleMenuItemClick("templates")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "templates"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
                        />
                      </svg>
                      <span className="text-sm">Templates</span>
                    </button>
                  </div>
                </div>

                {/* Explore Section */}
                <div className="mb-8">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
                    Explore
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleMenuItemClick("prompt-extractor")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "prompt-extractor"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span className="text-sm">Prompt Extractor</span>
                    </button>

                    <button
                      onClick={() => handleMenuItemClick("support")}
                      className={`w-full flex items-center space-x-3 p-2.5 rounded-full transition-all duration-200 ${
                        activeMenuItem === "support"
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium"
                          : "text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:font-semibold"
                      }`}
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm">Support</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto scrollbar-hide overscroll-none pt-[14px] transition-all duration-300 bg-black dark:bg-black">
            {activeMenuItem === "dashboard" ? (
              <div className="p-6 pb-32 md:pb-6 space-y-8">
                {/* What would you like to create today? Section */}
                <div className="relative bg-black dark:bg-black rounded-2xl border border-white/20 dark:border-white/20 p-6 md:p-8 animate-fade-in transition-colors duration-300 overflow-hidden">
                  {/* Background Image */}
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-15 dark:opacity-15"
                    style={{ backgroundImage: "url(/bg.png)" }}
                  ></div>

                  {/* Content */}
                  <div className="relative z-10 text-center mb-8 px-4">
                    <div className="w-full mb-3 overflow-visible flex justify-center">
                      <h2
                        className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white dark:text-white relative"
                        style={{ maxWidth: "100%" }}
                      >
                        <span className="animate-fade-in animation-delay-200">
                          Create Whatever You Can Imagine
                        </span>
                      </h2>
                    </div>
                    <p className="text-sm md:text-base text-white/70 dark:text-white/70 max-w-2xl mx-auto animate-slide-up animation-delay-100">
                      Turn text, images, or characters into stunning AI videos
                      with Pixelmaker.ai.
                    </p>
                  </div>

                  {/* Creation Options Grid */}
                  <div className="relative z-10 grid grid-cols-1 xl:grid-cols-3 gap-4 xl:gap-6 max-w-6xl mx-auto">
                    {/* Image to Video */}
                    <div
                      className="group relative overflow-hidden rounded-xl bg-black dark:bg-black border border-white/20 dark:border-white/20 hover:border-white/40 dark:hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 cursor-pointer hover:-translate-y-2 animate-slide-up animation-delay-200"
                      onClick={() => {
                        setActiveMenuItem("create-video");
                        setIsSidebarOpen(false);
                        navigate("/video/i2v");
                      }}
                    >
                      <div className="p-3 md:p-6 text-left md:text-center flex md:flex-col items-center justify-between md:justify-center">
                        <div className="flex items-center gap-3 md:flex-col md:gap-0">
                          <div className="w-8 h-8 md:w-12 md:h-12 md:mx-auto mb-0 md:mb-3 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                            <svg
                              className="w-4 h-4 md:w-6 md:h-6 text-black"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div className="md:text-center">
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                              Image to Video
                            </h3>

                            <div className="hidden md:inline-flex items-center text-white dark:text-white font-medium text-sm group-hover:text-gray-300 dark:group-hover:text-gray-300 transition-colors">
                              <span>Get Started</span>
                              <svg
                                className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="md:hidden">
                          <svg
                            className="w-6 h-6 text-white/60 group-hover:text-white transition-colors"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Templates */}
                    <div
                      className="group relative overflow-hidden rounded-xl bg-black dark:bg-black border border-white/20 dark:border-white/20 hover:border-white/40 dark:hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 cursor-pointer hover:-translate-y-2 animate-slide-up animation-delay-300"
                      onClick={() => {
                        setActiveMenuItem("templates");
                        setIsSidebarOpen(false);
                        navigate("/templates");
                      }}
                    >
                      <div className="p-3 md:p-6 text-left md:text-center flex md:flex-col items-center justify-between md:justify-center">
                        <div className="flex items-center gap-3 md:flex-col md:gap-0">
                          <div className="w-8 h-8 md:w-12 md:h-12 md:mx-auto mb-0 md:mb-3 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                            <svg
                              className="w-4 h-4 md:w-6 md:h-6 text-black"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z"
                              />
                            </svg>
                          </div>
                          <div className="md:text-center">
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                              Templates
                            </h3>

                            <div className="hidden md:inline-flex items-center text-white dark:text-white font-medium text-sm group-hover:text-gray-300 dark:group-hover:text-gray-300 transition-colors">
                              <span>Get Started</span>
                              <svg
                                className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="md:hidden">
                          <svg
                            className="w-6 h-6 text-white/60 group-hover:text-white transition-colors"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* No Filter */}
                    <div
                      className="group relative overflow-hidden rounded-xl bg-black dark:bg-black border border-white/20 dark:border-white/20 hover:border-white/40 dark:hover:border-white/40 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] dark:hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300 cursor-pointer hover:-translate-y-2 animate-slide-up animation-delay-400"
                      onClick={() => {
                        setActiveMenuItem("no-filter");
                        setIsSidebarOpen(false);
                        navigate("/no-filter");
                      }}
                    >
                      <div className="p-3 md:p-6 text-left md:text-center flex md:flex-col items-center justify-between md:justify-center">
                        <div className="flex items-center gap-3 md:flex-col md:gap-0">
                          <div className="w-8 h-8 md:w-12 md:h-12 md:mx-auto mb-0 md:mb-3 bg-white rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-[0_0_15px_rgba(255,255,255,0.5)]">
                            <svg
                              className="w-4 h-4 md:w-6 md:h-6 text-black"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                              />
                            </svg>
                          </div>
                          <div className="md:text-center">
                            <h3 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                              No Filter
                            </h3>

                            <div className="hidden md:inline-flex items-center text-white dark:text-white font-medium text-sm group-hover:text-gray-300 dark:group-hover:text-gray-300 transition-colors">
                              <span>Get Started</span>
                              <svg
                                className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        <div className="md:hidden">
                          <svg
                            className="w-6 h-6 text-white/60 group-hover:text-white transition-colors"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discord CTA Banner */}
                <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 text-white rounded-xl p-4 md:p-6 relative overflow-hidden animate-fade-in animation-delay-600 border border-white/20">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/5"></div>
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 md:gap-4 text-center md:text-left">
                      <div className="flex items-center justify-center w-16 h-16 md:w-12 md:h-12 bg-white/20 rounded-full">
                        <svg
                          className="w-8 h-8 md:w-6 md:h-6 text-white"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-xl font-bold mb-1">
                          Join Our Discord Community
                        </h3>
                        <p className="text-xs md:text-base text-white/90 leading-tight">
                          Connect with creators, get tips, and share your viral
                          videos
                        </p>
                      </div>
                    </div>
                    <div>
                      <button
                        onClick={() =>
                          window.open("https://discord.gg/PixelMaker", "_blank")
                        }
                        className="bg-white text-black hover:bg-gray-200 px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 whitespace-nowrap"
                      >
                        Join Discord
                      </button>
                    </div>
                  </div>
                </div>

                {/* Video Gallery Section */}
                <div className="animate-fade-in animation-delay-700">
                  <div className="text-left mb-6">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      Community
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300">
                      Get inspired by videos created with Pixelmaker.ai. Click
                      any video to see the prompt used.
                    </p>
                  </div>

                  {/* Video Grid - Masonry Style */}
                  <div className="columns-3 md:columns-4 lg:columns-5 xl:columns-6 2xl:columns-7 gap-3 md:gap-4 space-y-3 md:space-y-4">
                    {galleryVideosData.videos.map((video, index) => {
                      const durations = [
                        "0:20",
                        "1:02",
                        "0:28",
                        "1:01",
                        "1:24",
                        "1:26",
                        "0:25",
                        "0:38",
                        "1:00",
                      ];
                      const duration = durations[index % durations.length];

                      return (
                        <div
                          key={video.id}
                          className="group relative cursor-pointer rounded-xl overflow-hidden bg-gray-900 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 break-inside-avoid mb-3 md:mb-4 w-full"
                          onClick={() => handleGalleryVideoClick(video)}
                          style={{
                            aspectRatio: `${video.dimensions.width} / ${video.dimensions.height}`,
                          }}
                        >
                          {/* Video Thumbnail */}
                          <div className="relative overflow-hidden h-full w-full">
                            <img
                              src={video.thumbnail}
                              alt={video.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />

                            {/* Dark Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                            {/* Duration Badge */}
                            <div className="absolute top-3 right-3">
                              <span className="text-xs bg-black/80 text-white px-2 py-1 rounded font-medium">
                                {duration}
                              </span>
                            </div>

                            {/* Play Button Overlay */}
                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="w-12 h-12 bg-white/95 rounded-full flex items-center justify-center shadow-lg">
                                <svg
                                  className="w-6 h-6 text-gray-800 ml-0.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                                </svg>
                              </div>
                            </div>

                            {/* Title Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-3">
                              <h3 className="text-white font-semibold text-sm md:text-base leading-tight drop-shadow-lg">
                                {video.title}
                              </h3>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : activeMenuItem === "templates" ? (
              <Templates />
            ) : activeMenuItem === "create-video" || activeMenuItem === "template2v" ? (
              <Video />
            ) : activeMenuItem === "create-image" ? (
              <Image />
            ) : activeMenuItem === "assets" ? (
              <MyVideos />
            ) : activeMenuItem === "prompt-extractor" ? (
              <PromptExtractor />
            ) : activeMenuItem === "support" ? (
              <Support />
            ) : activeMenuItem === "no-filter" ? (
              location.pathname === "/no-filter/actors" ? (
                <NoFilterActors />
              ) : location.pathname === "/no-filter/examples" ? (
                <NoFilterExamples />
              ) : (
                <NoFilter />
              )
            ) : (
              <div className="p-4 md:p-6 lg:p-8 pb-32 md:pb-6">
                <div className="max-w-4xl mx-auto text-center">
                  <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 dark:text-white mb-4 tracking-tight">
                    Page Not Found
                  </h1>
                  <p className="text-gray-600 dark:text-gray-300 text-lg mb-8 font-light leading-relaxed">
                    The page you're looking for doesn't exist.
                  </p>
                  <button
                    onClick={() => handleMenuItemClick("dashboard")}
                    className="px-6 py-3 bg-white text-black font-semibold rounded-xl hover:bg-gray-200 transition-all"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Video Gallery Modal */}
      <VideoGalleryModal
        isOpen={isGalleryModalOpen}
        onClose={handleGalleryModalClose}
        video={selectedGalleryVideo}
      />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Router>
          <AppContent />
        </Router>
      </ToastProvider>
    </ErrorBoundary>
  );
}

export default App;
